import React, { useState, useMemo, useCallback } from 'react'

import {
  Toggle,
  Button,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, shape, bool, string } from 'prop-types'

import CodeIcon from 'images/icons/svg/shareable-snippet/CodeIcon'

const TOGGLE_OPTIONS = [
  { key: 'avatar', label: 'Avatar' },
  { key: 'modelTitle', label: 'Title' },
  { key: 'name', label: 'Name' },
  { key: 'views', label: 'Views' },
  { key: 'rating', label: 'Rating' },
  { key: 'likes', label: 'Likes' },
  { key: 'logo', label: 'Logo' },
  { key: 'button', label: 'Button' },
]

function CustomizeEmbed({
  embedUrl,
  embedButtonLabel,
  sharerUsername = '',
  settings,
  onSettingsChange: handleSettingsChange,
}) {
  const [isCopied, setIsCopied] = useState(false)

  const handleToggle = useCallback(
    (key) => (_, checked) => {
      setIsCopied(false)
      handleSettingsChange({ ...settings, [key]: checked })
    },
    [settings, handleSettingsChange]
  )

  const embedUrlWithParams = useMemo(() => {
    const disabledParams = Object.entries(settings)
      .filter(([, enabled]) => !enabled)
      .map(([key]) => `${key === 'modelTitle' ? 'title' : key}=0`)

    const allParams = sharerUsername
      ? [`sharer=${encodeURIComponent(sharerUsername)}`, ...disabledParams]
      : disabledParams

    return allParams.length > 0 ? `${embedUrl}?${allParams.join('&')}` : embedUrl
  }, [embedUrl, sharerUsername, settings])

  const iframeCode = useMemo(
    () => [
      `<iframe src="${embedUrlWithParams}"`,
      'style="width:100%; max-width:560px; aspect-ratio:7/5; border:none;"></iframe>',
    ].join(' '),
    [embedUrlWithParams]
  )

  const handleEmbedClick = useCallback(() => {
    navigator.clipboard.writeText(iframeCode)
    setIsCopied(true)
  }, [iframeCode])

  return (
    <div className="share-modal__customize-embed">
      <span className="share-modal__input-label">Customize your embed</span>
      <div className="share-modal__customize-embed-grid">
        {TOGGLE_OPTIONS.map(({ key, label }) => (
          <Toggle
            key={key}
            id={`customize-embed-${key}`}
            label={label}
            labelClassName="share-modal__toggle-label"
            isChecked={settings[key]}
            onChange={handleToggle(key)}
          />
        ))}
      </div>
      <Button
        type="secondary"
        icon={<Icon svgComponent={CodeIcon} />}
        customClass="share-modal__embed-button"
        onClick={handleEmbedClick}
      >
        {isCopied ? 'Copied!' : embedButtonLabel}
      </Button>
    </div>
  )
}

CustomizeEmbed.propTypes = {
  embedUrl: string.isRequired,
  embedButtonLabel: string.isRequired,
  sharerUsername: string,
  settings: shape({
    avatar: bool.isRequired,
    modelTitle: bool.isRequired,
    name: bool.isRequired,
    views: bool.isRequired,
    rating: bool.isRequired,
    likes: bool.isRequired,
    logo: bool.isRequired,
    button: bool.isRequired,
  }).isRequired,
  onSettingsChange: func.isRequired,
}

export default CustomizeEmbed
